import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Minus, Send, ThumbsUp, ThumbsDown, FileText, Loader2, Bot } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { streamChat, nsKey } from "@/lib/assistantChat";

export default function AssistantWidget() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const uid = user?.email || null;
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([]);
  const [dock, setDock] = useState(() => document.body.dataset.assistantDock || "");
  const scrollRef = useRef(null);
  const sessionId = useRef(null);
  const loadedFor = useRef(null);

  // Load per-user chat state (namespaced by user so sessions never leak across roles).
  useEffect(() => {
    if (!uid) { setMessages([]); sessionId.current = null; loadedFor.current = null; return; }
    if (loadedFor.current === uid) return;
    loadedFor.current = uid;
    try { setMessages(JSON.parse(localStorage.getItem(nsKey(uid, "msgs")) || "[]")); } catch { setMessages([]); }
    let s = localStorage.getItem(nsKey(uid, "session"));
    if (!s) {
      s = (crypto.randomUUID && crypto.randomUUID()) || `s_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(nsKey(uid, "session"), s);
    }
    sessionId.current = s;
  }, [uid]);

  useEffect(() => {
    if (uid && loadedFor.current === uid) {
      try { localStorage.setItem(nsKey(uid, "msgs"), JSON.stringify(messages.slice(-40))); } catch { /* ignore */ }
    }
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, uid]);

  useEffect(() => {
    const sync = () => setDock(document.body.dataset.assistantDock || "");
    window.addEventListener("assistant-dock", sync);
    return () => window.removeEventListener("assistant-dock", sync);
  }, []);

  const [modalOpen, setModalOpen] = useState(false);
  useEffect(() => {
    const check = () => setModalOpen(!!document.querySelector('[role="dialog"][data-state="open"]:not([data-assistant-panel])'));
    const mo = new MutationObserver(check);
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-state"] });
    return () => mo.disconnect();
  }, []);

  // Hide the assistant on the public rider-facing Explore pages.
  const onExplore = ["/explore", "/rider"].some((p) => pathname === p || pathname.startsWith(p + "/"));
  if (onExplore) return null;
  if (!user || !["admin", "shop"].includes(user.role)) return null;
  const roleLabel = user.role === "admin" ? "Admin" : "Partner";

  const setLast = (updater) =>
    setMessages((prev) => {
      const next = [...prev];
      const i = next.length - 1;
      next[i] = typeof updater === "function" ? updater(next[i]) : { ...next[i], ...updater };
      return next;
    });

  const send = async () => {
    const q = input.trim();
    if (!q || busy || !sessionId.current) return;
    setInput("");
    setBusy(true);
    setMessages((prev) => [
      ...prev,
      { role: "user", text: q },
      { role: "assistant", text: "", sources: [], logId: null, streaming: true },
    ]);

    try {
      await streamChat({
        sessionId: sessionId.current,
        message: q,
        handlers: {
          onSources: (s) => setLast((m) => ({ ...m, sources: s })),
          onDelta: (t) => setLast((m) => ({ ...m, text: (m.text || "") + t })),
          onError: (msg) => setLast((m) => ({ ...m, text: (m.text || "") + "\n\n⚠️ " + msg })),
          onDone: (id) => setLast((m) => ({ ...m, logId: id, streaming: false })),
        },
      });
      setLast((m) => ({ ...m, streaming: false }));
    } catch {
      setLast((m) => ({ ...m, text: m.text || "Sorry, something went wrong. Please try again.", streaming: false }));
    } finally {
      setBusy(false);
    }
  };

  const vote = async (logId, rating, msgIdx) => {
    if (!logId) return;
    try {
      await api.post("/assistant/feedback", { log_id: logId, rating });
      setMessages((prev) => prev.map((m, i) => (i === msgIdx ? { ...m, vote: rating } : m)));
    } catch { /* ignore */ }
  };

  return (
    <>
      {!open && !modalOpen && (
        <button
          data-testid="assistant-open-button"
          onClick={() => setOpen(true)}
          className={`fixed z-[1000] flex items-center gap-2 rounded-full bg-emerald-600 text-white shadow-lg shadow-emerald-900/20 transition hover:bg-emerald-700 hover:scale-[1.03] ${dock === "header" ? "top-[11px] right-14 h-10 w-10 justify-center lg:top-auto lg:bottom-20 lg:right-5 lg:h-auto lg:w-auto lg:px-4 lg:py-3" : "bottom-20 right-5 px-4 py-3"}`}
          data-dock={dock || "floating"}
          aria-label="Open Velopaths AI Assistant"
        >
          <Bot className="h-5 w-5" />
          <span className={`text-sm font-semibold ${dock === "header" ? "hidden lg:inline" : ""}`}>Ask Velo</span>
        </button>
      )}

      {open && (
        <div
          data-testid="assistant-panel"
          className="fixed bottom-5 right-5 z-[1000] flex h-[560px] max-h-[85vh] w-[380px] max-w-[92vw] flex-col overflow-hidden rounded-2xl border border-emerald-900/10 bg-white shadow-2xl"
        >
          <div className="flex items-start justify-between bg-emerald-700 px-4 py-3 text-white">
            <div>
              <div className="flex items-center gap-2 text-sm font-bold">
                <Bot className="h-4 w-4" /> Velopaths AI Assistant
              </div>
              <div className="mt-0.5 text-xs text-emerald-100" data-testid="assistant-role-indicator">
                Role: <span className="font-semibold">{roleLabel}</span> · read-only beta
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button data-testid="assistant-minimize-button" onClick={() => setOpen(false)} className="rounded p-1 hover:bg-emerald-600" aria-label="Minimize">
                <Minus className="h-4 w-4" />
              </button>
              <button data-testid="assistant-close-button" onClick={() => setOpen(false)} className="rounded p-1 hover:bg-emerald-600" aria-label="Close">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div ref={scrollRef} data-testid="assistant-messages" className="flex-1 space-y-3 overflow-y-auto bg-stone-50 px-3 py-3">
            {messages.length === 0 && (
              <div className="mt-6 text-center text-sm text-stone-500">
                <Bot className="mx-auto mb-2 h-8 w-8 text-emerald-600" />
                Ask me anything about using the portal — zones, advertisers, billing, routes, content and more.
                <div className="mt-2 text-xs text-stone-400">I only read the help docs and never change your account.</div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div
                  className={
                    "max-w-[88%] rounded-2xl px-3 py-2 text-sm " +
                    (m.role === "user" ? "bg-emerald-600 text-white" : "bg-white text-stone-800 shadow-sm ring-1 ring-stone-200")
                  }
                  data-testid={m.role === "user" ? "assistant-user-msg" : "assistant-reply-msg"}
                >
                  <div className="whitespace-pre-wrap">{m.text || (m.streaming ? "" : "")}</div>
                  {m.role === "assistant" && m.streaming && !m.text && (
                    <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />
                  )}
                  {m.role === "assistant" && m.sources && m.sources.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1" data-testid="assistant-sources">
                      {m.sources.map((s, j) => (
                        <span key={j} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-800 ring-1 ring-emerald-200" title={s.path}>
                          <FileText className="h-3 w-3" /> {s.title}
                        </span>
                      ))}
                    </div>
                  )}
                  {m.role === "assistant" && m.logId && !m.streaming && (
                    <div className="mt-2 flex items-center gap-2 text-stone-400">
                      <button
                        data-testid="assistant-thumbs-up"
                        onClick={() => vote(m.logId, "up", i)}
                        className={"rounded p-1 hover:text-emerald-600 " + (m.vote === "up" ? "text-emerald-600" : "")}
                        aria-label="Helpful"
                      >
                        <ThumbsUp className="h-3.5 w-3.5" />
                      </button>
                      <button
                        data-testid="assistant-thumbs-down"
                        onClick={() => vote(m.logId, "down", i)}
                        className={"rounded p-1 hover:text-rose-600 " + (m.vote === "down" ? "text-rose-600" : "")}
                        aria-label="Not helpful"
                      >
                        <ThumbsDown className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2 border-t border-stone-200 bg-white px-3 py-2">
            <input
              data-testid="assistant-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") send(); }}
              placeholder="Ask about zones, billing, routes…"
              className="flex-1 rounded-full border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 placeholder:text-stone-400 caret-stone-900 outline-none focus:border-emerald-500"
              disabled={busy}
            />
            <button
              data-testid="assistant-send-button"
              onClick={send}
              disabled={busy || !input.trim()}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-600 text-white transition hover:bg-emerald-700 disabled:opacity-40"
              aria-label="Send"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
